import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useT, useI18n } from "@/i18n";
import { usePageMeta } from "@/lib/format";
import { PageHeader } from "@/components/StatCard";
import { EmptyState } from "@/components/EmptyState";
import { Spinner } from "@/components/Spinner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { CalendarCheck, CalendarX, Video, XCircle, ExternalLink, Search } from "lucide-react";

const STATUS_DOT = {
  confirmed: "bg-emerald-500", paid: "bg-emerald-500",
  pending: "bg-primary", payment_pending: "bg-primary",
  cancelled: "bg-rose-500", pending_cancellation: "bg-amber-500",
  cancelled_refunded: "bg-rose-500", cancelled_no_refund: "bg-rose-500", cancelled_refund_pending: "bg-rose-500",
};
const CANCELLED = ["cancelled", "cancelled_refunded", "cancelled_no_refund", "cancelled_refund_pending"];

export default function MyBookings() {
  const t = useT();
  const { locale } = useI18n();
  const { refreshUser } = useAuth();
  usePageMeta(t("bkc.bookings_title"));
  const navigate = useNavigate();
  const [bookings, setBookings] = useState(null);
  const [tab, setTab] = useState("upcoming");
  const [cancelling, setCancelling] = useState(null);

  const load = useCallback(async () => {
    try {
      const [prods, offs] = await Promise.all([
        api.get("/coach/bookings/mine").then((r) => r.data).catch(() => []),
        api.get("/coach/offerings/bookings/mine").then((r) => r.data).catch(() => []),
      ]);
      const normalised = [
        ...(prods || []).map((b) => ({
          id: b.id, kind: "product", title: b.product_name || t("bkc.session_title"),
          coach_name: b.coach_name || "", start_iso: b.start_at || null,
          duration_min: b.duration_min || null, status: b.status || "confirmed",
          price_cents: b.price_cents || 0, price_credits: b.price_credits || 0,
          payment_mode: b.payment_mode || "cash",
        })),
        ...(offs || []).map((b) => ({
          id: b.id, kind: "offering", title: b.offering_title || b.title || t("bkc.session_title"),
          coach_name: b.coach_name || "", start_iso: b.scheduled_at || null,
          duration_min: null, status: b.status || "paid", price_cents: b.price_cents || 0,
          policy_refund_pct: b.policy_refund_pct, refund_pct_applied: b.refund_pct_applied,
          refund_amount_cents: b.refund_amount_cents, cancel_response_note: b.cancel_response_note,
          zoom_join_url: b?.zoom?.join_url || null,
        })),
      ];
      normalised.sort((a, b) => {
        const ta = a.start_iso ? new Date(a.start_iso).getTime() : Number.MAX_SAFE_INTEGER;
        const tb = b.start_iso ? new Date(b.start_iso).getTime() : Number.MAX_SAFE_INTEGER;
        return ta - tb;
      });
      setBookings(normalised);
    } catch { setBookings([]); }
  }, [t]);

  useEffect(() => { load(); }, [load]);

  const now = Date.now();
  const isPast = (b) => {
    if (CANCELLED.includes(b.status)) return true;
    if (!b.start_iso) return false;
    return new Date(b.start_iso).getTime() < now;
  };

  const cancelProduct = async (b) => {
    if (!window.confirm(t("bkc.cancel_confirm"))) return;
    try { await api.post(`/coach/bookings/${b.id}/cancel`); toast.success(t("bkc.cancel_sent")); await load(); await refreshUser(); }
    catch (e) { toast.error(apiError(e)); }
  };

  if (bookings === null) return <Spinner label={t("common.loading")} />;

  const upcoming = bookings.filter((b) => !isPast(b));
  const past = bookings.filter(isPast);
  const rows = tab === "upcoming" ? upcoming : past;

  return (
    <div className="space-y-6" data-testid="client-bookings-screen">
      <PageHeader
        title={t("bkc.bookings_title")}
        subtitle={t("bkc.bookings_sub")}
        action={
          <Button variant="outline" className="gap-1.5 rounded-full" asChild data-testid="bookings-browse">
            <Link to="/app/coach-hub"><Search className="h-4 w-4" /> {t("nav.coach_hub")}</Link>
          </Button>
        }
      />

      {bookings.length === 0 ? (
        <EmptyState
          icon={CalendarCheck}
          title={t("bkc.none")}
          description={t("bkc.none_sub")}
          action={<Button className="rounded-full" asChild data-testid="bookings-empty-browse"><Link to="/app/coach-hub">{t("nav.coach_hub")}</Link></Button>}
        />
      ) : (
        <>
          <div className="inline-flex rounded-full border border-border bg-card p-1">
            {[["upcoming", upcoming.length], ["past", past.length]].map(([k, n]) => (
              <button
                key={k}
                data-testid={`bookings-tab-${k}`}
                onClick={() => setTab(k)}
                className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${tab === k ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}
              >
                {t(`bkc.${k}`)} · {n}
              </button>
            ))}
          </div>

          {rows.length === 0 ? (
            <div className="flex flex-col items-center gap-2 rounded-2xl border border-dashed border-border bg-secondary/40 px-6 py-12 text-center">
              {tab === "upcoming" ? <CalendarCheck className="h-8 w-8 text-sage-light" /> : <CalendarX className="h-8 w-8 text-sage-light" />}
              <p className="text-sm text-muted-foreground">{tab === "upcoming" ? t("bkc.no_upcoming") : t("bkc.no_past")}</p>
            </div>
          ) : (
            <div className="space-y-3">
              {rows.map((b) => (
                <BookingRow key={`${b.kind}-${b.id}`} b={b} t={t} locale={locale} navigate={navigate}
                  onCancel={() => b.kind === "offering" ? setCancelling(b) : cancelProduct(b)} />
              ))}
            </div>
          )}
        </>
      )}

      <CancelRefundModal booking={cancelling} t={t} onClose={() => setCancelling(null)}
        onDone={async () => { setCancelling(null); await load(); }} />
    </div>
  );
}

function BookingRow({ b, t, locale, navigate, onCancel }) {
  const clickable = b.kind === "offering";
  const canCancel = ["paid", "confirmed", "pending", "payment_pending"].includes(b.status) && !isPastStatus(b);
  const when = b.start_iso
    ? new Date(b.start_iso).toLocaleString(locale, { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })
    : t("bkc.time_tba");

  return (
    <div className="rounded-2xl border border-border bg-card p-5" data-testid={`booking-${b.id}`}>
      <div className="flex items-center gap-2">
        <span className={`h-2 w-2 rounded-full ${STATUS_DOT[b.status] || "bg-muted-foreground"}`} />
        <span className="text-[11px] uppercase tracking-wide text-muted-foreground">{t(`bkc.status_${b.status}`)}</span>
        {b.kind === "offering" ? <span className="rounded-full bg-accent px-2 py-0.5 text-[10px] text-accent-foreground">{t("bkc.marketplace")}</span> : null}
      </div>
      <h3 className="mt-2 font-display text-lg text-foreground">{b.title}</h3>
      <p className="text-sm text-muted-foreground">
        {t("bkc.with_coach", { name: b.coach_name })}{b.duration_min ? ` · ${b.duration_min} ${t("bkc.min")}` : ""}
      </p>
      <p className="mt-1 text-sm text-primary">{when}</p>
      {b.status === "cancelled_refunded" && b.refund_pct_applied != null ? (
        <p className="mt-1 text-xs text-emerald-600">{t("bkc.refund_pct", { pct: b.refund_pct_applied })}</p>
      ) : null}
      {b.cancel_response_note ? <p className="mt-1 text-xs italic text-muted-foreground">&ldquo;{b.cancel_response_note}&rdquo;</p> : null}

      {(clickable || canCancel || b.zoom_join_url) && (
        <div className="mt-3 flex flex-wrap gap-2">
          {clickable ? (
            <Button variant="outline" size="sm" className="gap-1.5 rounded-full" onClick={() => navigate(`/app/session/${b.id}`)} data-testid={`booking-open-${b.id}`}>
              <ExternalLink className="h-3.5 w-3.5" /> {t("bkc.open_details")}
            </Button>
          ) : null}
          {b.zoom_join_url && (b.status === "paid" || b.status === "confirmed") ? (
            <Button variant="outline" size="sm" className="gap-1.5 rounded-full" onClick={() => window.open(b.zoom_join_url, "_blank")} data-testid={`booking-join-zoom-${b.id}`}>
              <Video className="h-3.5 w-3.5" /> {t("bkc.join_zoom")}
            </Button>
          ) : null}
          {canCancel ? (
            <Button variant="ghost" size="sm" className="gap-1.5 rounded-full text-rose-600 hover:text-rose-700" onClick={onCancel} data-testid={`cancel-booking-${b.id}`}>
              <XCircle className="h-3.5 w-3.5" /> {b.kind === "offering" ? t("bkc.cancel_request") : t("bkc.cancel")}
            </Button>
          ) : null}
        </div>
      )}
    </div>
  );
}

function isPastStatus(b) {
  if (CANCELLED.includes(b.status)) return true;
  if (!b.start_iso) return false;
  return new Date(b.start_iso).getTime() < Date.now();
}

function CancelRefundModal({ booking, t, onClose, onDone }) {
  const [preview, setPreview] = useState(null);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!booking) { setPreview(null); setReason(""); return; }
    api.get(`/coach/offerings/bookings/${booking.id}/refund-preview`)
      .then((r) => setPreview(r.data)).catch(() => setPreview(null));
  }, [booking]);

  if (!booking) return null;
  const pct = preview?.refund_pct ?? booking.policy_refund_pct ?? 0;
  const amountCents = preview?.refund_amount_cents ?? Math.round((booking.price_cents || 0) * pct / 100);
  const hours = preview?.hours_to_session;

  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/coach/offerings/bookings/${booking.id}/request-cancel`, { reason: reason.trim() });
      toast.success(t("bkc.cancel_sent"));
      onDone();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={!!booking} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent data-testid="refund-conditions-modal">
        <DialogHeader>
          <DialogTitle>{t("bkc.refund_title")}</DialogTitle>
          <DialogDescription>{t("bkc.refund_intro")}</DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <TierRow txt={t("bkc.refund_tier_72")} active={pct >= 100} color="bg-emerald-500" />
          <TierRow txt={t("bkc.refund_tier_24")} active={pct >= 50 && pct < 100} color="bg-primary" />
          <TierRow txt={t("bkc.refund_tier_now")} active={pct < 50} color="bg-rose-500" />
        </div>
        <div className="rounded-xl border border-primary bg-accent/40 p-4" data-testid="refund-preview-box">
          <p className="text-[11px] uppercase tracking-wide text-primary">{t("bkc.refund_you_get")}</p>
          <p className="mt-1 font-display text-2xl font-semibold text-foreground">{t("bkc.refund_pct", { pct: Math.round(pct) })}</p>
          <p className="text-sm text-primary">{t("bkc.refund_amount", { amount: (amountCents / 100).toFixed(2) + " €" })}</p>
          {hours != null ? <p className="mt-1 text-xs text-muted-foreground">{t("bkc.refund_hours_left", { hours: Math.max(0, hours).toFixed(1) })}</p> : null}
        </div>
        <p className="text-xs italic text-muted-foreground">{t("bkc.refund_note")}</p>
        <Textarea
          data-testid="cancel-reason-input"
          placeholder={t("bkc.reason_ph")}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          maxLength={500}
          className="min-h-[70px]"
        />
        <DialogFooter>
          <Button variant="destructive" className="rounded-full" disabled={busy} onClick={submit} data-testid="submit-cancel-request">
            {busy ? t("common.saving") : t("bkc.submit_cancel")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function TierRow({ txt, active, color }) {
  return (
    <div className={`flex items-center gap-2 rounded-xl border px-3 py-2 ${active ? "border-primary bg-accent/40" : "border-border"}`}>
      <span className={`h-2 w-2 rounded-full ${color}`} />
      <span className={`text-xs ${active ? "font-semibold text-foreground" : "text-muted-foreground"}`}>{txt}</span>
    </div>
  );
}
